import { useCallback } from "react";
import { useApiUrl } from "@refinedev/core";
import { useUploadFiles } from "#src/hooks";
import { toUploadFile } from "pages/create-experience/helpers/fileList";

/**
 * Adds a single document to an already-existing project — the Edit Experience
 * page's "Add a document" action (Extras only, see `DocumentsEditCard`).
 *
 * Mirrors the upload half of `useCreateProjectWithDocuments` (same
 * `POST /media/projects/{id}/documents/{file_name}` endpoint via
 * `useUploadFiles`), minus the create-project and aggregate-merge steps that
 * only apply at creation time. Always sends `hidden: true` — Extras is the
 * only editable list on this page, so there's no other value it could be.
 */
export const useAddProjectDocument = (projectId: string) => {
  const apiUrl = useApiUrl();

  const { getS3LinksToUploadFiles, uploadFileToS3 } = useUploadFiles({
    assetRequestProperties: { document_type: "Unknown", hidden: true },
    stagedAssets: undefined,
  });

  const addProjectDocument = useCallback(
    async (file: File) => {
      const uploadFile = toUploadFile(file, 0);
      const customUrl = (fileName: string) =>
        `${apiUrl}/media/projects/${projectId}/documents/${fileName}`;
      const [s3Link] = await getS3LinksToUploadFiles({
        fileListToUpload: [uploadFile],
        customUrl,
      });
      if (!s3Link) throw new Error("No S3 link provided");
      await Promise.all(uploadFileToS3([uploadFile], [s3Link]));
    },
    [apiUrl, projectId, getS3LinksToUploadFiles, uploadFileToS3]
  );

  return { addProjectDocument };
};
